import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition, faAddressBook, faSitemap, faUserEdit, faUserPlus } from '@fortawesome/pro-duotone-svg-icons';
import { faArrowAltRight, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { SortDescriptor } from '@progress/kendo-data-query';
import _ from 'lodash';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, shareReplay, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { getEntryValueForColumn } from 'src/app/helper/directoryEntryHelper';
import { getTopmostTableCellElement, isTargetingGridHeader } from 'src/app/helper/elementAncestorFunctions';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { Key } from 'src/app/samwebDescription/samweb/shortcuts/Keys';
import { ShortcutTypeDirectory } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeDirectory';
import { ShortcutTypeGlobal } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeGlobal';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
// tslint:disable: max-line-length
import {
   DirectoryBrowserChangeCustomSorting,
   DirectoryBrowserEntrySelected,
   DirectoryBrowserMakeCall,
   DirectoryBrowserSelectNextEntry,
   DirectoryCallEnded,
   DirectoryNewIncomingCall,
   DirectoryRowDoubleClicked,
   DirectorySearchInGivenColumns,
   DirectorySearchValueChanged,
   EmptyDirectory,
   LoadMoreDirectoryValues,
   SetIsCallTriggeredDirectoryAssociationsFilterActive,
   SetIsCallerFilterActive,
   ToggleDirectoryFilterForCaller,
   ToggleDirectoryFilterForIncomingCall,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.actions';
// tslint:enable: max-line-length
import { CellClickEvent } from '@progress/kendo-angular-grid';
import escapeStringRegexp from 'escape-string-regexp';
import {
   DirectoryBrowserBehaviourSelectors,
   DirectoryData,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.selectors';
import { DirectoryBrowserBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.state';
import { DirectoryDataBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-data/directory-data-behaviour.state';
import {
   DirectoryBrowserAddEntry,
   DirectoryBrowserEditEntry,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.actions';
import { DirectoryEditingBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.state';
import { DirectoryHelperState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-helper/directory-helper.state';
import { DirectorySettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { DashboardState } from 'src/app/store/dashboard/dashboard.state';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import { SelectedSubscriberChanged } from 'src/app/store/samwin/directory/selected-subscriber/selected-subscriber.actions';
import { SelectedSubscriberState } from 'src/app/store/samwin/directory/selected-subscriber/selected-subscriber.state';
import { NextDialTargetChanged } from 'src/app/store/samwin/next-dial-target/next-dial-target.actions';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SettingsSelectors } from 'src/app/store/settings/settings.selectors';
import { RestoreWindow, SetWindowIndex } from 'src/app/store/utility/samweb-window/samweb-window.actions';
import { SamwebWindowState, WindowTypes } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { DirectoryEntryRemoteDto } from 'src/generated/swagger';
import { DirectoryBrickSettingsComponent } from './directory-brick-settings/directory-brick-settings.component';

@Component({
   selector: 'samweb-directory-brick',
   templateUrl: './directory-brick.component.html',
   styleUrls: ['./directory-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'directoryBrick',
   brickTitleTranslationKey: 'brick.brick_definitions.directory_browser.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.directory_browser.description',
   brickIcon: faAddressBook,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: DirectoryBrickSettingsComponent,
   settingStateHandler: DirectorySettingsState,
})
export class DirectoryBrickComponent extends BasicBrickComponent implements OnInit, OnDestroy, AfterViewInit {
   get gridContent(): Element {
      return _(this.element.nativeElement.getElementsByClassName('k-grid-content')).head();
   }
   get gridDisplayHeight(): number {
      return this.element.nativeElement.querySelector('.k-virtual-content')?.offsetHeight;
   }
   get gridRowHeight(): number {
      return this.element.nativeElement.querySelector('td')?.offsetHeight ?? 0;
   }
   get gridPageSize(): number {
      return _.round(this.gridDisplayHeight / this.gridRowHeight);
   }

   private destroy$ = new Subject();

   // Time span in ms for buffering observable updates.
   // Should be applied to all observables that trigger the rendering of the grid.
   public readonly renderBufferTime: number = 60;

   @Select(DirectoryStructureState.availableDirectoryColumnsOrdered)
   public availableColumns$: Observable<DirectoryColumn[]>;

   public callableColumns$: Observable<string[]>;

   public orgFilterIcon: IconDefinition = faSitemap;
   public callerFilterIcon: IconDefinition = faPhone;
   public callerFilterIcon2: IconDefinition = faArrowAltRight;

   public userAddIcon: IconDefinition = faUserPlus;
   public userEditIcon: IconDefinition = faUserEdit;

   public directorySearchResult$: Observable<DirectoryEntryRemoteDto[]>;

   public directorySearchValue$: Observable<string>;

   public directorySearchHighlightRegExp$: Observable<RegExp>;

   public selectedKeys$: Observable<number[]>;

   public sortDescription$: Observable<SortDescriptor[]>;

   public wasSearchEverExecuted$: Observable<boolean>;

   public clearSearchBoxOnIncomingCallEnabled$: Observable<boolean>;

   public filterOnCallingSubscriberEnabled$: Observable<boolean>;
   public filterOnCallingSubscriberActive$: Observable<boolean>;
   public filterOnCallingSubscriberPossible$: Observable<boolean>;

   public filterOnSkillOrgAssociationEnabled$: Observable<boolean>;
   public filterOnSkillOrgAssociationActive$: Observable<boolean>;
   public filterOnSkillOrgAssociationPossible$: Observable<boolean>;

   public isEntrySelected$: Observable<boolean>;

   public isPhonetic$: Observable<boolean>;

   public searchableColumns$: Observable<DirectoryColumn[]>;

   public defaultCallableColumn$: Observable<DirectoryColumn>;

   public availableDirectoryData$: Observable<DirectoryData[]>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT))
   public isAttendant$: Observable<boolean>;

   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;

   constructor(private store: Store, private element: ElementRef) {
      super();
   }

   ngAfterViewInit(): void {
      const autoSearchColumnKey$ = this.store.select(DirectorySettingsState.areSettingsFromServerReceived).pipe(
         filter((areSettingsFromServerReceived) => areSettingsFromServerReceived),
         switchMap(() =>
            this.store.select(DirectorySettingsState.getAutoSearchColumnKey).pipe(
               map((fn) => fn(this.brickInstanceId)),
               distinctUntilChanged()
            )
         )
      );

      combineLatest([
         this.store.select(DirectoryBrowserBehaviourSelectors.getIsQueryingDirectoryPossible).pipe(
            map((fn) => fn(this.brickInstanceId)),
            distinctUntilChanged()
         ),
         autoSearchColumnKey$,
      ])
         .pipe(
            filter(([isDirectoryQueryingPossible, autoSearchColumnKey]) => isDirectoryQueryingPossible && _.isNil(autoSearchColumnKey)),
            take(1),
            tap(() => this.store.dispatch(new LoadMoreDirectoryValues(this.brickInstanceId))),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnInit() {
      this.availableDirectoryData$ = this.store.select(DirectoryBrowserBehaviourSelectors.getDirectoryValues).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual),
         shareReplay(1)
      );

      this.directorySearchResult$ = this.store.select(DirectoryDataBehaviourState.getDirectoryEntries).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual),
         shareReplay(1)
      );

      this.directorySearchValue$ = this.store.select(DirectoryBrowserBehaviourState.getSearchValue).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(),
         shareReplay(1)
      );

      this.directorySearchHighlightRegExp$ = this.store.select(DirectoryBrowserBehaviourState.getSearchValuesSplitted).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         debounceTime(this.renderBufferTime),
         distinctUntilChanged(_.isEqual),
         map((searchValues) => {
            if (_.isEmpty(searchValues)) {
               return null;
            }
            return new RegExp(
               _(searchValues)
                  .filter((x) => !_.isEmpty(x))
                  .map((x) => _.escape(x))
                  .map((x) => escapeStringRegexp(x))
                  .value()
                  .join('|'),
               'gi'
            );
         }),
         shareReplay(1)
      );

      this.searchableColumns$ = this.store.select(DirectoryBrowserBehaviourSelectors.getColumnsForCurrentSearch).pipe(
         map((searchableColumns) => searchableColumns(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual),
         shareReplay(1)
      );

      this.filterOnCallingSubscriberEnabled$ = this.store.select(DirectorySettingsState.getAutomaticRestrictForIncomingCaller).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(),
         shareReplay(1)
      );

      this.filterOnCallingSubscriberActive$ = this.store.select(DirectoryBrowserBehaviourState.getIsCallerFilterActive).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged()
      );

      this.filterOnCallingSubscriberPossible$ = this.store.select(DirectoryBrowserBehaviourSelectors.getIsCallerFilterPossible).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged()
      );

      this.filterOnSkillOrgAssociationEnabled$ = this.store.select(DirectorySettingsState.getAutomaticFilterOnSkillOrganisationAssociations).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(),
         shareReplay(1)
      );

      this.filterOnSkillOrgAssociationActive$ = this.store.select(DirectoryBrowserBehaviourState.getIsCallTriggeredDirectoryAssociationsFilterActive).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged()
      );

      this.filterOnSkillOrgAssociationPossible$ = this.store.select(DirectoryBrowserBehaviourSelectors.getIsFilterOnSkillOrganisationAssociationsPossible).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged()
      );

      this.clearSearchBoxOnIncomingCallEnabled$ = this.store.select(DirectorySettingsState.getClearSearchBoxOnIncomingCall).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(),
         shareReplay(1)
      );

      this.selectedKeys$ = this.store.select(DirectoryBrowserBehaviourState.getSelectedDirectoryIDs).pipe(
         map((fn) => fn(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual),
         shareReplay(1)
      );

      this.isEntrySelected$ = this.store.select(DirectoryBrowserBehaviourState.getIsEntrySelected).pipe(
         map((fn) => fn(this.brickInstanceId)),
         distinctUntilChanged()
      );

      this.defaultCallableColumn$ = this.store.select(DirectorySettingsState.getDefaultCallColumn).pipe(
         map((fn) => fn(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual),
         shareReplay(1)
      );

      this.callableColumns$ = this.store.select(DirectorySettingsState.getAvailableCallColumnKeys).pipe(
         map((x) => x(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual),
         shareReplay(1)
      );

      this.sortDescription$ = this.store.select(DirectoryBrowserBehaviourSelectors.getSortDescription).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged(_.isEqual)
      );

      this.wasSearchEverExecuted$ = this.store.select(DirectoryDataBehaviourState.wasSearchExecuted).pipe(
         map((fn) => fn(this.brickInstanceId)),
         shareReplay(1)
      );

      /** If any call related filter is enabled, dispatch @see DirectoryNewIncomingCall action to adapt filters. */
      combineLatest([this.filterOnSkillOrgAssociationEnabled$, this.filterOnCallingSubscriberEnabled$, this.clearSearchBoxOnIncomingCallEnabled$])
         .pipe(
            filter((enabledFiltersAndSettings) => _.some(enabledFiltersAndSettings)),
            distinctUntilChanged(),
            switchMap(() =>
               this.store
                  .select(SamwinLinesStateSelector.getRingingCallId)
                  .pipe(
                     filter((callId) => !_.isNil(callId)),
                     distinctUntilChanged()
                  )
                  .pipe(
                     switchMap((callId) => {
                        return this.store.dispatch(new DirectoryNewIncomingCall(this.brickInstanceId, callId));
                     })
                  )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      /** Dispatch @see DirectoryCallEnded action if a call has ended. */
      this.store
         .select(SamwinLinesStateSelector.getSourceLineState)
         .pipe(
            pairwise(),
            filter(([oldValue, newValue]) => !_.isNil(oldValue) && oldValue !== LineStateEnum.IDLE && newValue === LineStateEnum.IDLE),
            tap(() => this.store.dispatch(new DirectoryCallEnded(this.brickInstanceId))),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // When filtering for skill organisation association gets disabled,
      // we have to push those changes into our behavior state to immediately apply changes without having to re-logon.
      this.filterOnSkillOrgAssociationEnabled$
         .pipe(
            filter((setting) => setting === false),
            takeUntil(this.destroy$)
         )
         .subscribe(() => {
            this.store.dispatch(new SetIsCallTriggeredDirectoryAssociationsFilterActive(this.brickInstanceId, false));
         });

      // When filtering for incoming caller gets disabled,
      // we have to push those changes into our behavior state to immediately apply changes without having to re-logon.
      this.filterOnCallingSubscriberEnabled$
         .pipe(
            filter((setting) => setting === false),
            takeUntil(this.destroy$)
         )
         .subscribe(() => {
            this.store.dispatch(new SetIsCallerFilterActive(this.brickInstanceId, false));
         });

      // publish Next Dial Target when the selected entry changes
      this.selectedKeys$
         .pipe(
            withLatestFrom(this.directorySearchResult$),
            distinctUntilChanged(([prevSelectedKeys, prevData], [curSelectedKeys, curData]) => {
               const prevSelectedEntry = _.find(prevData, (x) => x.id === _.first(prevSelectedKeys));
               const curSelectedEntry = _.find(curData, (x) => x.id === _.first(curSelectedKeys));
               return prevSelectedEntry?.orgnum === curSelectedEntry?.orgnum;
            }),
            tap(([selectedKeys, data]) => {
               const isFocussed = this.store.selectSnapshot(DashboardState.isBrickFocussedFn)(this.brickInstanceId);
               if (!isFocussed) {
                  return;
               }
               const selectedEntry = _.find(data, (x) => x.id === _.first(selectedKeys));

               this.changeDialTarget(selectedEntry);
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(DirectorySettingsState.getDefaultListeningColumn)
         .pipe(
            map((getColumn) => getColumn(this.brickInstanceId)),
            distinctUntilChanged(),
            filter((searchingColumn) => !_.isNil(searchingColumn)),
            switchMap((searchingColumn: DirectoryColumn) =>
               this.store.select(DirectoryHelperState.getSelectedEntryForSearch).pipe(
                  distinctUntilChanged(),
                  filter((entry) => !_.isNil(entry)),
                  map((entry) =>
                     typeof entry[searchingColumn.columnTyp.directoryMapValue] === 'string'
                        ? entry[searchingColumn.columnTyp.directoryMapValue]
                        : entry[searchingColumn.columnTyp.directoryMapValue].value
                  ),
                  filter((value) => value !== this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSearchValue)(this.brickInstanceId)),
                  tap((value) => {
                     if (!_.isEmpty(value)) {
                        this.store.dispatch(new DirectorySearchValueChanged(this.brickInstanceId, value));
                        return;
                     }
                     this.store.dispatch(new EmptyDirectory(this.brickInstanceId));
                  })
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // after a search yields no results, publish the search text as Next Dial Target to allow dialing it
      this.wasSearchEverExecuted$
         .pipe(withLatestFrom(this.directorySearchResult$), distinctUntilChanged(), takeUntil(this.destroy$))
         .subscribe(([isSearching, data]) => {
            if (!isSearching && _.isEmpty(data)) {
               const searchValue = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSearchValue)(this.brickInstanceId);
               this.store.dispatch(
                  new NextDialTargetChanged(
                     {
                        number: searchValue,
                     },
                     this.brickInstanceId
                  )
               );
            }
         });

      // (re-)publish Next Dial Target on focus
      this.store
         .select(DashboardState.isBrickFocussedFn)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            takeUntil(this.destroy$)
         )
         .subscribe((hasFocus) => {
            if (hasFocus) {
               const selectedEntry = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSelectedEntry)(this.brickInstanceId);
               if (this.brickInstanceId !== this.store.selectSnapshot(SelectedSubscriberState.selectionSetByBrickInstanceId)) {
                  this.store.dispatch(new SelectedSubscriberChanged(selectedEntry, this.brickInstanceId));
               }
               this.changeDialTarget(selectedEntry);
            }
         });

      combineLatest([
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowUp),
            action: () => {
               console.debug('[HotKey] Directory Pressed ArrowUp');
               this.store.dispatch(new DirectoryBrowserSelectNextEntry(this.brickInstanceId, 'up'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowDown),
            action: () => {
               console.debug('[HotKey] Directory Pressed ArrowDown');
               this.store.dispatch(new DirectoryBrowserSelectNextEntry(this.brickInstanceId, 'down'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageUp),
            action: () => {
               console.debug('[HotKey] Directory Pressed PageUp');
               this.store.dispatch(new DirectoryBrowserSelectNextEntry(this.brickInstanceId, 'up', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageDown),
            action: () => {
               console.debug('[HotKey] Directory Pressed PageDown');
               this.store.dispatch(new DirectoryBrowserSelectNextEntry(this.brickInstanceId, 'down', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         this.getShortcutDescription$(this.store.select(SettingsSelectors.getHotkeyForAction).pipe(map((fn) => fn(ShortcutTypeGlobal.MAKE_CALL.value))), () => {
            const doDialAndAnswerShareHotkeys = this.store.selectSnapshot(SettingsSelectors.doDialAndAnswerShareHotkeys);
            const isCallRingingAndAcceptable = this.store.selectSnapshot(ClientPhoneState.isCallRingingAndAcceptable);
            if (doDialAndAnswerShareHotkeys && isCallRingingAndAcceptable) {
               return false;
            }
            const directoryEntryToCall = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSelectedEntry)(this.brickInstanceId);
            console.debug('[HotKey] Directory Pressed MAKE_CALL', directoryEntryToCall);
            this.store.dispatch(new DirectoryBrowserMakeCall(this.brickInstanceId, directoryEntryToCall));
            return true;
         }),
         this.getShortcutDescription$(
            this.store
               .select(DirectorySettingsState.getHotkeyForAction)
               .pipe(map((fn) => fn(this.brickInstanceId, ShortcutTypeDirectory.DIAL_NUMBER_COLUMN.enumName))),
            () => {
               return ShortcutTypeDirectory.DIAL_NUMBER_COLUMN.hotkeyAction(this.store, this.brickInstanceId);
            }
         ),
         this.getShortcutDescription$(
            this.store
               .select(DirectorySettingsState.getHotkeyForAction)
               .pipe(map((fn) => fn(this.brickInstanceId, ShortcutTypeDirectory.DIAL_MOBILE_COLUMN.enumName))),
            () => {
               return ShortcutTypeDirectory.DIAL_MOBILE_COLUMN.hotkeyAction(this.store, this.brickInstanceId);
            }
         ),
         this.getShortcutDescription$(
            this.store
               .select(DirectorySettingsState.getHotkeyForAction)
               .pipe(map((fn) => fn(this.brickInstanceId, ShortcutTypeDirectory.DIAL_ALTERNATIVE_DEVICE_COLUMN.enumName))),
            () => {
               return ShortcutTypeDirectory.DIAL_ALTERNATIVE_DEVICE_COLUMN.hotkeyAction(this.store, this.brickInstanceId);
            }
         ),
         this.getShortcutDescription$(
            this.store
               .select(DirectorySettingsState.getHotkeyForAction)
               .pipe(map((fn) => fn(this.brickInstanceId, ShortcutTypeDirectory.OPEN_APPOINTMENT_DIALOG.enumName))),
            () => {
               return ShortcutTypeDirectory.OPEN_APPOINTMENT_DIALOG.hotkeyAction(this.store, this.brickInstanceId);
            }
         ),
         this.getShortcutDescription$(
            this.store
               .select(DirectorySettingsState.getHotkeyForAction)
               .pipe(map((fn) => fn(this.brickInstanceId, ShortcutTypeDirectory.ADD_DIRECTORY_ENTRY.enumName))),
            () => {
               return ShortcutTypeDirectory.ADD_DIRECTORY_ENTRY.hotkeyAction(this.store, this.brickInstanceId);
            }
         ),
         this.getShortcutDescription$(
            this.store
               .select(DirectorySettingsState.getHotkeyForAction)
               .pipe(map((fn) => fn(this.brickInstanceId, ShortcutTypeDirectory.EDIT_DIRECTORY_ENTRY.enumName))),
            () => {
               return ShortcutTypeDirectory.EDIT_DIRECTORY_ENTRY.hotkeyAction(this.store, this.brickInstanceId);
            }
         ),
      ])
         .pipe(
            tap((shortcuts) => {
               this.keyActions = shortcuts;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isPhonetic$ = this.store.select(DirectoryBrowserBehaviourState.getIsSearchPhonetic).pipe(
         map((isPhonetic) => isPhonetic(this.brickInstanceId)),
         distinctUntilChanged()
      );
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   loadMoreDirectoryValuesFn() {
      return () => {
         if (!this.store.selectSnapshot(DirectoryDataBehaviourState.getIsAllDataLoaded)(this.brickInstanceId)) {
            this.store.dispatch(new LoadMoreDirectoryValues(this.brickInstanceId));
         }
      };
   }

   searchTextChanged(value: string) {
      this.store.dispatch(new DirectorySearchValueChanged(this.brickInstanceId, value));
   }

   kendoCellClick(event: CellClickEvent) {
      this.changeDialTargetOnClick(event);

      // trigger column based search on right-click
      if (event.type === 'contextmenu') {
         event.originalEvent.preventDefault();
         event.originalEvent.stopPropagation();

         // trigger selection change as it is prevented above
         this.store.dispatch(new DirectoryBrowserEntrySelected(this.brickInstanceId, event.dataItem.id));

         // find clicked column
         const column = _.find(
            this.store.selectSnapshot(DirectorySettingsState.getAllPossibleSearchableColumns),
            (x) => x.directoryColumnKey === event.column.field
         );
         if (_.isNil(column)) {
            // clicked in non-searchable column
            return;
         }
         this.triggerColumnBasedSearch(event.dataItem, column, this.brickInstanceId);
      }
   }

   kendoCellDoubleClick(event: CellClickEvent) {
      if (isTargetingGridHeader(event.originalEvent)) {
         return;
      }

      this.store.dispatch(new DirectoryRowDoubleClicked(this.brickInstanceId, event.dataItem, event.column.field));
   }

   private changeDialTargetOnClick(clickEvent: CellClickEvent) {
      const dataItem = clickEvent?.dataItem as DirectoryEntryRemoteDto;
      const columnFieldId = clickEvent?.column?.field;

      const rowContent = this.store.selectSnapshot(DirectorySettingsState.getSelectedOrDefaultCallColumn)(this.brickInstanceId, dataItem, columnFieldId);
      this.store.dispatch(
         new NextDialTargetChanged(
            {
               number: rowContent?.value?.toString(),
               name: dataItem.orgnam,
               subscriberId: dataItem?.id,
            },
            this.brickInstanceId
         )
      );
   }

   private changeDialTarget(selectedEntry: DirectoryEntryRemoteDto) {
      const rowContent = this.store.selectSnapshot(DirectorySettingsState.getDefaultOrAvailableCallColumn)(this.brickInstanceId, selectedEntry);
      const searchValue = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSearchValue)(this.brickInstanceId);
      this.store.dispatch(
         new NextDialTargetChanged(
            {
               number: _.isNil(selectedEntry) ? searchValue : rowContent?.value?.toString(),
               name: selectedEntry?.orgnam,
               subscriberId: selectedEntry?.id,
            },
            this.brickInstanceId
         )
      );
   }

   protected handleFocusChanged(hasFocus: boolean): boolean {
      if (hasFocus) {
         // (re-)publish Next Dial Target on focus
         const selectedEntry = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSelectedEntry)(this.brickInstanceId);
         this.store.dispatch(new DirectoryBrowserEntrySelected(this.brickInstanceId, selectedEntry.id));
      }

      return false;
   }

   selectedKeysChange(selectedEntryIds: number[]) {
      const selectedEntryId = _.head(selectedEntryIds);
      if (_.isNil(selectedEntryId)) {
         return;
      }

      this.store.dispatch(new DirectoryBrowserEntrySelected(this.brickInstanceId, selectedEntryId));
   }

   public sortChange(sortDescription: SortDescriptor[]): void {
      this.store.dispatch(new DirectoryBrowserChangeCustomSorting(this.brickInstanceId, sortDescription));
   }

   toggleIncomingCallFilter() {
      const isFilteringPossible = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getIsFilterOnSkillOrganisationAssociationsPossible)(
         this.brickInstanceId
      );
      this.store.dispatch(new ToggleDirectoryFilterForIncomingCall(this.brickInstanceId, isFilteringPossible));
   }

   toggleCallerFilter() {
      this.store.dispatch(new ToggleDirectoryFilterForCaller(this.brickInstanceId));
   }

   addEntry() {
      this.store.dispatch(new DirectoryBrowserAddEntry(this.brickInstanceId));
   }

   editEntry() {
      if (!this.store.selectSnapshot(DirectoryEditingBehaviourState.isCurrentEntryEdited)(this.brickInstanceId)) {
         this.store.dispatch(new DirectoryBrowserEditEntry(this.brickInstanceId));
      } else {
         const index = this.store.selectSnapshot(SamwebWindowState.getHighestIndex);
         const windowId = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSelectedDirectoryID)(this.brickInstanceId).toString();
         this.store.dispatch(new SetWindowIndex(WindowTypes.Directory, windowId, index + 1));
         this.store.dispatch(new RestoreWindow(WindowTypes.Directory, windowId));
      }
   }

   scrollToSelectedRow() {
      const index = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSelectedDirectoryIndex)(this.brickInstanceId);
      const selectedRowPosition = this.gridRowHeight * index;
      const halfWindowHeight = this.gridDisplayHeight / 2 - this.gridRowHeight / 2;
      const scrollPosition = selectedRowPosition - halfWindowHeight;
      this.gridContent.scrollTo({ top: scrollPosition });
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      const tooltipAnchor = getTopmostTableCellElement(element);
      if (tooltipAnchor && (tooltipAnchor.scrollWidth > tooltipAnchor.parentElement.offsetWidth || tooltipAnchor.offsetWidth < tooltipAnchor.scrollWidth)) {
         this.cellTooltipDirective.toggle(tooltipAnchor);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }

   call(column: DirectoryColumn, entry: DirectoryEntryRemoteDto) {
      const numberToCall = getEntryValueForColumn(this.store, entry, column);

      this.store.dispatch(new StartNewCallAction(numberToCall.toString()));
   }

   columnHeaderSearchChanged(column: DirectoryColumn, brickId: string) {
      const entry = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSelectedEntry)(brickId);
      this.triggerColumnBasedSearch(entry, column, brickId);
   }

   private triggerColumnBasedSearch(entry: DirectoryEntryRemoteDto, column: DirectoryColumn, brickId: string) {
      // Designed Behaviour:
      // * if entry is given, use value of entry for clicked column header
      // * if no entry is given (i.e. column header clicked while grid is empty), take value from search box
      //   this allows toggling of column based search while no results are present
      const searchValue = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSearchValue)(this.brickInstanceId);
      const valueToSearch = getEntryValueForColumn(this.store, entry, column) ?? searchValue;
      this.store.dispatch(new DirectorySearchInGivenColumns(brickId, valueToSearch, [column]));
   }
}
